import { useNavigate } from 'react-router-dom';
import { BackendContext } from '../Context/BackendContext';
import LogInRequiredModal from './LogInRequiredModal';
import React, { useState, useEffect, useRef, useContext } from "react";
import { Avatar, Divider } from "@heroui/react";
import {
  MessageCircle,
  MessageCircleX,
  Users,
} from 'lucide-react';
import { getPlayerProgress } from '../helper/playerProgress';

const MOBILE_CHAT_BREAKPOINT = 768;
const isMobileChatViewport = () => typeof window !== 'undefined' && window.innerWidth < MOBILE_CHAT_BREAKPOINT;

const Chat = ({ currentPage }) => {
  const { userProfile, updatePresence, fetchOnlineUsers, subscribeToMessages, createMessage, subscribeToChat, fetchRecentMessages, fetchUserByUsername } = useContext(BackendContext);
  const navigate = useNavigate();

  const [isCollapsed, setIsCollapsed] = useState(() => isMobileChatViewport());
  const [pageMessages, setPageMessages] = useState([]);
  const [globalMessages, setGlobalMessages] = useState([]);
  const [chatTab, setChatTab] = useState("global");
  const [message, setMessage] = useState("");
  const [pageOnlineUsers, setPageOnlineUsers] = useState([]);
  const [globalOnlineUsers, setGlobalOnlineUsers] = useState([]);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [hoveredUser, setHoveredUser] = useState(null);
  const [hoveredProfile, setHoveredProfile] = useState(null);
  const [hoverCardLoading, setHoverCardLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const seenMessageIdsRef = useRef(new Set());
  const profileCacheRef = useRef(new Map());
  const hoveredUserRef = useRef(null);
  const hoverClearTimeoutRef = useRef(null);

  const appendUniqueMessage = (setter, rec) => {
    const messageKey = rec?.id ?? `${rec?.username ?? 'unknown'}:${rec?.timestamp ?? ''}:${rec?.content ?? ''}:${rec?.page ?? 'global'}`;
    if (seenMessageIdsRef.current.has(messageKey)) return;
    seenMessageIdsRef.current.add(messageKey);
    setter((prev) => [...prev, rec]);
  };

  useEffect(() => {
    const handleViewportChange = () => {
      setIsCollapsed(isMobileChatViewport());
    };

    handleViewportChange();
    window.addEventListener('resize', handleViewportChange);

    return () => window.removeEventListener('resize', handleViewportChange);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [pageMessages, globalMessages]);

  useEffect(() => {
    let unsubscribeMessages = null;
    let unsubscribeChat = null;
    let disposed = false;

    if (chatTab === 'global') {
      seenMessageIdsRef.current = new Set();
      setGlobalMessages([]);
    } else {
      seenMessageIdsRef.current = new Set();
      setPageMessages([]);
    }

    const scope = chatTab === 'global' ? 'global' : 'page';
    const pageParam = chatTab === 'page' ? currentPage : undefined;

    (async () => {
      try {
        const recent = await fetchRecentMessages({ scope, page: pageParam });
        if (disposed) return;

        seenMessageIdsRef.current = new Set(
          recent.map((rec) => rec?.id ?? `${rec?.username ?? 'unknown'}:${rec?.timestamp ?? ''}:${rec?.content ?? ''}:${rec?.page ?? 'global'}`)
        );

        if (scope === 'global') {
          setGlobalMessages(recent);
        } else {
          setPageMessages(recent);
        }

        const messageUnsub = await subscribeToMessages({ page: pageParam });
        if (disposed) {
          messageUnsub?.();
          return;
        }
        unsubscribeMessages = messageUnsub;

        unsubscribeChat = subscribeToChat((evt) => {
          const kind = evt?.kind;
          const rec = evt?.record ?? evt;
          if (!rec || kind !== 'Chat') return;

          if (scope === 'global' && !rec.page) {
            appendUniqueMessage(setGlobalMessages, rec);
          } else if (scope === 'page' && rec.page === currentPage) {
            appendUniqueMessage(setPageMessages, rec);
          }
        });
      } catch (e) {
        if (!disposed) {
          console.error('subscribe setup failed:', e);
        }
      }
    })();

    return () => {
      disposed = true;
      unsubscribeMessages?.();
      unsubscribeChat?.();
    };
  }, [chatTab, currentPage, fetchRecentMessages, subscribeToMessages, subscribeToChat]);

  useEffect(() => {
    let cancelled = false;

    const updateUserPresence = async () => {
      try {
        await updatePresence(true, currentPage);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to update presence:', error);
        }
      }
    };

    updateUserPresence();
    const interval = setInterval(updateUserPresence, 60000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [currentPage, updatePresence]);

  useEffect(() => {
    let alive = true;

    const refreshOnlineUsers = async () => {
      try {
        const allOnline = (await fetchOnlineUsers()) ?? [];
        if (!alive) return;

        const uniqueGlobal = Array.from(new Map(allOnline.map((entry) => [entry.userSub, entry])).values());
        const pageScopedUsers = uniqueGlobal.filter((user) => user.context === currentPage);

        setGlobalOnlineUsers(uniqueGlobal);
        setPageOnlineUsers(pageScopedUsers);
      } catch (error) {
        if (alive) {
          console.error('Failed to fetch online users:', error);
        }
      }
    };

    refreshOnlineUsers();
    const interval = setInterval(refreshOnlineUsers, 30000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, [currentPage, fetchOnlineUsers]);

  const handleSend = async (event) => {
    event.preventDefault();

    if (!userProfile) {
      setLoginModalOpen(true);
      return;
    }

    const content = message.trim();
    if (!content) return;

    const newMessage = {
      username: userProfile.username,
      content,
      page: chatTab === "page" ? currentPage : null,
    };

    await createMessage(newMessage);
    setMessage("");
  };

  const visibleOnlineUsers = chatTab === 'global' ? globalOnlineUsers : pageOnlineUsers;
  const visibleMessages = chatTab === "global" ? globalMessages : pageMessages;

  const handleUserHover = async (username) => {
    if (!username) return;

    clearTimeout(hoverClearTimeoutRef.current);
    hoveredUserRef.current = username;
    setHoveredUser(username);
    const cached = profileCacheRef.current.get(username);
    if (cached) {
      setHoveredProfile(cached);
      setHoverCardLoading(false);
      return;
    }

    setHoveredProfile(null);
    setHoverCardLoading(true);
    try {
      const profile = await fetchUserByUsername(username);
      if (!profile) return;
      profileCacheRef.current.set(username, profile);
      if (hoveredUserRef.current === username) {
        setHoveredProfile(profile);
      }
    } catch (error) {
      console.error('Failed to fetch hover profile:', error);
    } finally {
      if (hoveredUserRef.current === username) {
        setHoverCardLoading(false);
      }
    }
  };

  const clearHoveredUser = () => {
    clearTimeout(hoverClearTimeoutRef.current);
    hoverClearTimeoutRef.current = setTimeout(() => {
      hoveredUserRef.current = null;
      setHoveredUser(null);
      setHoveredProfile(null);
      setHoverCardLoading(false);
    }, 120);
  };

  return (
    <div
      className={`fixed top-20 bottom-0 z-50 shadow-lg rounded-l-xl flex flex-col bg-gray-900 border-l border-gray-700 transition-all duration-300 ${
        isCollapsed ? 'right-[-325px] w-[325px]' : 'right-0 w-[325px]'
      }`}
    >
      <div
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute left-[-32px] top-3 w-8 h-10 bg-gray-800 text-white rounded-l-md shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-700"
      >
        {isCollapsed ? <MessageCircle /> : <MessageCircleX />}
      </div>

      <div className="flex px-4 pt-3 pb-2 space-x-4 border-b border-gray-800">
        <button onClick={() => setChatTab("global")} className={`text-sm ${chatTab === "global" ? "text-white font-bold" : "text-gray-400"}`}>
          Global
        </button>
        <Divider orientation="vertical"/>
        <button onClick={() => setChatTab("page")} className={`text-sm ${chatTab === "page" ? "text-white font-bold" : "text-gray-400"}`}>
          Page: {currentPage}
        </button>
      </div>

      <div className="flex items-center px-4 py-2 text-sm text-gray-400 border-b border-gray-800">
        <Users className="mr-2 h-4 w-4" />
        <div className="relative h-3 w-3 mr-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
          <span className="absolute inline-flex h-2 w-2 rounded-full bg-green-500 top-[2px] left-[2px]"></span>
        </div>
        <span>{visibleOnlineUsers.length} online</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {visibleMessages.map((msg, idx) => {
          const cachedProfile = profileCacheRef.current.get(msg.username)
            || (userProfile?.username === msg.username ? userProfile : null);
          const { level } = getPlayerProgress(cachedProfile);
          const isHovered = hoveredUser === msg.username;
          const hoverProfileData = isHovered ? (hoveredProfile || cachedProfile) : null;
          const hoverProgress = getPlayerProgress(hoverProfileData);

          return (
            <div
              key={idx}
              className="relative"
              onMouseEnter={() => { handleUserHover(msg.username); }}
              onMouseLeave={clearHoveredUser}
            >
              <span
                className="font-semibold text-white cursor-pointer hover:underline"
                onClick={() => {navigate(`/User/${msg.username}`)}}
              >
                {msg.username}
              </span>
              <span className="ml-2 rounded-full border border-indigo-500/40 bg-indigo-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-200">
                Lv {level}
              </span>
              : <span className="text-gray-300">{msg.content}</span>
              {isHovered && (
                <div className="absolute left-0 top-full z-20 mt-2 min-w-[220px] rounded-2xl border border-slate-700 bg-slate-950/95 p-3 shadow-2xl backdrop-blur">
                  {hoverCardLoading && !hoverProfileData ? (
                    <div className="text-sm text-slate-400">Loading player...</div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Avatar
                        size="lg"
                        isBordered
                        src={hoverProfileData?.avatarImageData ? `data:image/png;base64,${hoverProfileData.avatarImageData}` : undefined}
                        name={hoverProfileData?.username?.substring(0, 2) || msg.username?.substring(0, 2)}
                        className="shrink-0"
                        style={{ imageRendering: "pixelated" }}
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-white">
                          {hoverProfileData?.username || msg.username}
                        </div>
                        <div className="mt-1 text-xs text-indigo-200">
                          Level {hoverProgress.level}
                        </div>
                        <div className="mt-1 text-[11px] text-slate-400">
                          {hoverProgress.totalPixels.toLocaleString()} pixels placed
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSend} className="p-4 border-t border-gray-800">
        <input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your message..."
          className="w-full bg-gray-800 text-white px-3 py-2 rounded focus:outline-none"
        />
      </form>
      <LogInRequiredModal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        message="You must be logged in to send messages."
      />
    </div>
  );
};

export default Chat;
